import {
  ReputationScoreConfidence,
  type ReputationAnalyticsDto,
  type SentimentTrendGranularity,
} from '@up2stay/up2promo-api-client';
import {
  Alert,
  AlertDescription,
  Badge,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  cn,
  type ChartConfig,
} from '@up2stay/up2promo-ui';
import { Info } from 'lucide-react';
import type { ComponentProps } from 'react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  XAxis,
  YAxis,
} from 'recharts';

import {
  deltaCount,
  deltaRate,
  pct,
} from '@/core/components/reputation/analytics/analytics-format';
import { formatTrendLabel } from '@/core/components/reputation/reputation-bits';
import { CHART } from '@/core/components/reputation/reputation-chart-config';
import { buildTrendAxisTicks } from '@/core/components/reputation/reputation-chart-utils';
import { formatReputationScoreValue } from '@/core/components/reputation/reputation-score-formula';
import {
  REPUTATION_INFO_ALERT_CLASS,
  REPUTATION_INFO_ALERT_TEXT_CLASS,
  REPUTATION_SECTION_TITLE_CLASS,
} from '@/core/components/reputation/reputation-surface';
import { useIsMobile } from '@/core/hooks/use-mobile';
import type { Dictionary } from '@/core/providers/dictionary-provider';

export type AnalyticsMetric = 'reviews' | 'score' | 'positive' | 'response';

const METRICS: AnalyticsMetric[] = ['reviews', 'score', 'positive', 'response'];

type PanelDict = Dictionary['reputation']['analytics'];

function positiveShare(data: ReputationAnalyticsDto): number {
  return data.totalReviews > 0
    ? data.sentimentSplit.positive / data.totalReviews
    : 0;
}

function metricValue(
  metric: AnalyticsMetric,
  data: ReputationAnalyticsDto,
): string {
  switch (metric) {
    case 'reviews':
      return data.totalReviews.toLocaleString();
    case 'score':
      return formatReputationScoreValue(data.score);
    case 'positive':
      return pct(positiveShare(data));
    case 'response':
      return pct(data.responseRateWithText);
  }
}

function metricDelta(
  metric: AnalyticsMetric,
  data: ReputationAnalyticsDto,
  prev: ReputationAnalyticsDto,
) {
  switch (metric) {
    case 'reviews':
      return deltaCount(data.totalReviews, prev.totalReviews);
    case 'score':
      return deltaCount(data.score.score, prev.score.score);
    case 'positive':
      return deltaRate(positiveShare(data), positiveShare(prev));
    case 'response':
      return deltaRate(data.responseRateWithText, prev.responseRateWithText);
  }
}

function DeltaPill({ direction, text }: { direction: number; text: string }) {
  const arrow = direction > 0 ? '↗' : direction < 0 ? '↘' : '=';
  return (
    <Badge
      variant={
        direction > 0 ? 'success' : direction < 0 ? 'warning' : 'secondary'
      }
      className="text-[11.5px] font-semibold max-md:border-0 max-md:bg-transparent max-md:p-0 max-md:text-[10.5px]"
    >
      {arrow} {text}
    </Badge>
  );
}

type Point = {
  label: string;
  positive: number;
  neutral: number;
  negative: number;
  previous?: number | null;
  value?: number | null;
};

function buildPoints(
  data: ReputationAnalyticsDto,
  prev: ReputationAnalyticsDto | undefined,
  metric: AnalyticsMetric,
  locale: string,
): Point[] {
  const granularity = data.trend.granularity as SentimentTrendGranularity;
  return data.trend.points.map((point, index) => {
    const total = point.positive + point.neutral + point.negative;
    const base: Point = {
      label: formatTrendLabel(point.date, granularity, locale),
      positive: point.positive,
      neutral: point.neutral,
      negative: point.negative,
    };
    const prevTrend = prev?.trend.points[index];
    const prevKpi = prev?.kpiSeries[index];
    const kpi = data.kpiSeries[index];
    switch (metric) {
      case 'reviews':
        return {
          ...base,
          previous: prevTrend
            ? prevTrend.positive + prevTrend.neutral + prevTrend.negative
            : undefined,
        };
      case 'score':
        return { ...base, value: kpi?.score, previous: prevKpi?.score };
      case 'response':
        return {
          ...base,
          value: kpi ? kpi.responseRateWithText * 100 : null,
          previous: prevKpi ? prevKpi.responseRateWithText * 100 : undefined,
        };
      case 'positive': {
        const prevTotal = prevTrend
          ? prevTrend.positive + prevTrend.neutral + prevTrend.negative
          : 0;
        return {
          ...base,
          value: total > 0 ? (point.positive / total) * 100 : null,
          previous:
            prevTrend && prevTotal > 0
              ? (prevTrend.positive / prevTotal) * 100
              : undefined,
        };
      }
    }
  });
}

/**
 * KPI tab strip + trend chart. The active KPI drives the chart: review volume
 * renders as sentiment-stacked buckets, the rest as running lines; the previous
 * period is overlaid as a dashed line when comparison is on.
 */
export function AnalyticsTrendPanel({
  data,
  prev,
  metric,
  onMetricChange,
  labels,
  locale,
  vsLabel,
}: {
  data: ReputationAnalyticsDto;
  /** Comparison-period analytics; undefined when comparison is off. */
  prev: ReputationAnalyticsDto | undefined;
  metric: AnalyticsMetric;
  onMetricChange: (metric: AnalyticsMetric) => void;
  labels: PanelDict;
  locale: string;
  vsLabel: string;
}) {
  const points = buildPoints(data, prev, metric, locale);
  const ticks = buildTrendAxisTicks(points);
  const lowConfidence =
    data.score.scoreConfidence === ReputationScoreConfidence.LOW;
  const isVolume = metric === 'reviews';
  const isMobile = useIsMobile();

  const config = {
    positive: { label: labels.sentiment.positive, color: CHART.positive },
    neutral: { label: labels.sentiment.neutral, color: CHART.neutral },
    negative: { label: labels.sentiment.negative, color: CHART.negative },
    value: {
      label: labels.metrics[metricKey(metric)],
      color: 'var(--primary)',
    },
    previous: { label: vsLabel, color: 'var(--text-faint)' },
  } satisfies ChartConfig;

  const metricLabels: Record<AnalyticsMetric, string> = {
    reviews: labels.metrics.totalReviews,
    score: labels.metrics.reputationScore,
    positive: labels.metrics.positiveShare,
    response: labels.metrics.responseRate,
  };
  const chartTitle: Record<AnalyticsMetric, string> = {
    reviews: labels.trend.reviewsPerBucket,
    score: labels.trend.scoreOverTime,
    positive: labels.trend.positiveOverTime,
    response: labels.trend.responseOverTime,
  };

  return (
    <div className="flex min-w-0 flex-col gap-3 md:gap-0 md:overflow-hidden md:rounded-[var(--radius-card)] md:border md:bg-card md:text-card-foreground md:shadow-[var(--shadow-card)]">
      <div
        role="tablist"
        aria-label={labels.title}
        className="flex gap-2 md:grid md:grid-cols-2 md:gap-0 md:border-b lg:grid-cols-4"
      >
        {METRICS.map((key, index) => {
          const active = key === metric;
          const delta = prev ? metricDelta(key, data, prev) : null;
          return (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => onMetricChange(key)}
              className={cn(
                // Mobile: three separate KPI cards (response rate is desktop-only).
                'flex min-w-0 flex-1 basis-0 flex-col items-start gap-0.5 rounded-2xl border bg-card p-3 text-left shadow-[var(--shadow-card)] transition-colors',
                key === 'response' && 'max-md:hidden',
                active &&
                  'max-md:border-2 max-md:border-primary max-md:p-[11px]',
                // md+: tab strip inside the shared card.
                'md:gap-1.5 md:rounded-none md:border-0 md:border-t-[3px] md:px-[22px] md:py-[18px] md:shadow-none',
                index % 2 === 1 && 'md:border-l',
                index >= 2 && 'max-lg:md:border-t-border',
                index > 0 && 'lg:border-l',
                active
                  ? 'md:border-t-primary md:bg-accent'
                  : 'md:border-t-transparent md:hover:bg-muted/30',
              )}
            >
              <span className="text-muted-foreground text-[11px] md:text-[12.5px]">
                {metricLabels[key]}
              </span>
              <span className="flex flex-wrap items-baseline gap-x-2.5 gap-y-0.5">
                <span className="text-2xl font-bold leading-none tabular-nums md:text-[30px]">
                  {metricValue(key, data)}
                </span>
                {delta ? (
                  <DeltaPill direction={delta.direction} text={delta.text} />
                ) : null}
              </span>
            </button>
          );
        })}
      </div>

      <div className="rounded-[var(--radius-card-mobile)] border bg-card p-[18px] shadow-[var(--shadow-card)] md:rounded-none md:border-0 md:bg-transparent md:px-6 md:pb-4 md:pt-5 md:shadow-none">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h2 className={REPUTATION_SECTION_TITLE_CLASS}>
            {chartTitle[metric]}
          </h2>
          <div className="text-muted-foreground flex flex-wrap items-center gap-3.5 text-xs max-md:hidden">
            {isVolume
              ? (['positive', 'neutral', 'negative'] as const).map((key) => (
                  <span key={key} className="flex items-center gap-1.5">
                    <span
                      className="size-2 rounded-[2px]"
                      style={{ background: CHART[key] }}
                    />
                    {labels.sentiment[key]}
                  </span>
                ))
              : null}
            {prev ? (
              <span className="flex items-center gap-1.5">
                <span className="w-3.5 border-t-2 border-dashed border-[color:var(--text-faint)]" />
                {vsLabel}
              </span>
            ) : null}
          </div>
        </div>

        {points.length === 0 ? (
          <p className="text-muted-foreground py-16 text-center text-sm">
            {labels.trend.empty}
          </p>
        ) : (
          <ChartContainer
            config={config}
            className="aspect-auto h-[150px] w-full md:h-[220px]"
          >
            <ComposedChart
              accessibilityLayer
              data={points}
              margin={{ left: 0, right: 8, top: 8, bottom: 0 }}
            >
              <CartesianGrid
                vertical={false}
                strokeDasharray="3 3"
                className="stroke-border/50"
              />
              <XAxis
                dataKey="label"
                tickLine={false}
                axisLine={false}
                tickMargin={10}
                ticks={ticks}
                interval={0}
                minTickGap={24}
              />
              <YAxis
                hide={isMobile}
                width={32}
                tickLine={false}
                axisLine={false}
                allowDecimals={false}
                domain={isVolume ? [0, 'auto'] : [0, 100]}
              />
              <ChartTooltip
                cursor={{ stroke: 'var(--border)', strokeDasharray: '4 4' }}
                content={(tooltipProps) => (
                  <ChartTooltipContent
                    {...(tooltipProps as ComponentProps<
                      typeof ChartTooltipContent
                    >)}
                    indicator="dot"
                  />
                )}
              />
              {isVolume ? (
                <>
                  <Bar
                    dataKey="positive"
                    stackId="s"
                    fill="var(--color-positive)"
                    maxBarSize={isMobile ? 30 : 44}
                    isAnimationActive={false}
                  />
                  <Bar
                    dataKey="neutral"
                    stackId="s"
                    fill="var(--color-neutral)"
                    maxBarSize={isMobile ? 30 : 44}
                    isAnimationActive={false}
                  />
                  <Bar
                    dataKey="negative"
                    stackId="s"
                    fill="var(--color-negative)"
                    radius={[6, 6, 0, 0]}
                    maxBarSize={isMobile ? 30 : 44}
                    isAnimationActive={false}
                  />
                </>
              ) : (
                <Line
                  dataKey="value"
                  type="monotone"
                  stroke="var(--color-value)"
                  strokeWidth={2.5}
                  dot={false}
                  connectNulls
                  isAnimationActive={false}
                />
              )}
              {prev ? (
                <Line
                  dataKey="previous"
                  type="monotone"
                  stroke="var(--color-previous)"
                  strokeWidth={2}
                  strokeDasharray="5 4"
                  strokeOpacity={0.7}
                  dot={false}
                  connectNulls
                  isAnimationActive={false}
                />
              ) : null}
            </ComposedChart>
          </ChartContainer>
        )}

        {lowConfidence ? (
          <Alert
            variant="info"
            className={cn(REPUTATION_INFO_ALERT_CLASS, 'mt-4')}
          >
            <Info aria-hidden />
            <AlertDescription className={REPUTATION_INFO_ALERT_TEXT_CLASS}>
              {labels.lowConfidence.replace(
                '{count}',
                String(data.totalReviews),
              )}
            </AlertDescription>
          </Alert>
        ) : null}
      </div>
    </div>
  );
}

function metricKey(
  metric: AnalyticsMetric,
): 'totalReviews' | 'reputationScore' | 'positiveShare' | 'responseRate' {
  switch (metric) {
    case 'reviews':
      return 'totalReviews';
    case 'score':
      return 'reputationScore';
    case 'positive':
      return 'positiveShare';
    case 'response':
      return 'responseRate';
  }
}
